import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { useScheduleService } from '../../../../../../context/ScheduleServiceContext';
import { ScheduleServiceQuery } from '../../../../../../../../services/query';
import { SDD, sddCls } from './detailSection.classes';

interface DetailItem {
    title: string;
    value: any;
    onClick?: any;
    renderValue?: string;
    rowClass?: string;
}

interface ScheduleGroup {
    groupId: any;
    groupName: string;
}

const getActiveStatusValue = (isEnabled: boolean) => {
    return (
        <Tooltip className={SDD.statusIconTooltip} title="Active">
            {isEnabled ? (
                <CheckCircleIcon className={SDD.statusIconSuccess} fontSize="large" color="success" />
            ) : (
                <CancelIcon className={SDD.statusIconError} fontSize="large" color="error" />
            )}
        </Tooltip>
    );
};

const DetailRow: React.FC<DetailItem> = ({ title, value, renderValue = null, onClick, rowClass }) => (
    <Box
        className={sddCls(SDD.detailRow, rowClass)}
        sx={{
            marginBottom: '12px',
            padding: '8px 16px',
            borderRadius: '8px',
            background: '#f2f2f2',
        }}
        onClick={onClick}
    >
        <Typography className={SDD.detailTitle} sx={{ fontWeight: 'bold', fontSize: '14px', color: '#555' }}>
            {title}
        </Typography>
        <Tooltip className={SDD.detailValueTooltip} title={typeof value === 'string' ? value : ''}>
            <Typography
                className={SDD.detailValue}
                sx={{
                    fontSize: '16px',
                    color: '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                    borderBottom: '1px dotted #999',
                }}
            >
                {renderValue || value}
            </Typography>
        </Tooltip>
    </Box>
);

const StatusBox: React.FC<{ title: string; value: React.ReactNode }> = ({ title, value }) => (
    <Box className={SDD.statusBox} sx={{ textAlign: 'center', padding: 0 }}>
        <Typography className={SDD.statusTitle} sx={{ fontWeight: 'bold', fontSize: '12px', color: '#777', marginBottom: '4px' }}>
            {title}
        </Typography>
        <span className={SDD.statusValue}>{value}</span>
    </Box>
);

export const DetailSection: React.FC = () => {
    const [scheduleGroups, setScheduleGroups] = useState<ScheduleGroup[]>([]);
    const { selectedJob } = useScheduleService();

    const { t } = useTranslation();

    const detailData: DetailItem[] = [
        {
            title: t('scheduleService.id'),
            rowClass: SDD.detailRowJobId,
            value: selectedJob?.jobId || '',
            onClick: async (event: React.MouseEvent) => {
                event.stopPropagation();
                await navigator.clipboard.writeText(selectedJob?.jobId);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'info',
                    title: t('general.copied'),
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });
            },
        },
        { title: t('scheduleService.jobName'), rowClass: SDD.detailRowJobName, value: selectedJob?.jobName || '' },
        {
            title: t('scheduleService.team'),
            rowClass: SDD.detailRowTeam,
            value: (scheduleGroups || []).find((item: any) => item.groupId === selectedJob?.ownerTeamId)?.groupName,
        },
        {
            title: t('scheduleService.email'),
            rowClass: SDD.detailRowEmail,
            value: selectedJob?.emailSettings?.toEmails?.join(', '),
            renderValue: selectedJob?.emailSettings?.toEmails?.[0],
        },
    ];

    useEffect(() => {
        ScheduleServiceQuery.GetScheduleGroups()
            .then((res: any) => {
                if (res) {
                    setScheduleGroups(res.data);
                }
            })
            .catch(() => Swal.fire(t('general.failed'), '', 'error'));
    }, []);

    return (
        <Box className={SDD.root} sx={{ width: '100%' }}>
            <Box
                className={SDD.header}
                sx={{
                    textAlign: 'center',
                    marginBottom: '16px',
                    borderBottom: '2px solid #ccc',
                    paddingBottom: '8px',
                }}
            >
                <Typography
                    className={SDD.headerTitle}
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '18px',
                        color: '#333',
                    }}
                >
                    {t('scheduleService.detail')}
                </Typography>
            </Box>
            {detailData.map((item, index) => (
                <DetailRow key={index} {...item} />
            ))}
            <Box
                className={SDD.statusRow}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: '#f2f2f2',
                    padding: '8px 16px',
                    borderRadius: '8px',
                }}
            >
                <StatusBox
                    title={t('scheduleService.isEnabled')}
                    value={getActiveStatusValue(selectedJob?.isEnabled)}
                />
            </Box>
        </Box>
    );
};
